import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { deleteCustomer } from "../../api/customer";
import { findMetaData } from "../../helpers/metadataValidation";
import DeleteIcon from "@mui/icons-material/Delete";
import { makeStyles } from "@mui/styles";
import ConfirmBox from "../general/confirmBox";
import { padding } from "@mui/system";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: "auto",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100vh - 230px)",
    marginTop: "-70px",
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--primary-light-blue-color)",
      color: "#000",
    },
    "& td": {
      padding: "2px 4px 2px 10px",
    },
  },
  headerRow: {
    backgroundColor: "var(--primary-blue-color)",
    color: "#ffffff !important",
    "& th": {
      padding: "9px",
      color: "#fff",
    },
  },
  headerColumn: {
    color: "#ffffff !important",
    "&:hover": {
      color: "#000",
    },
  },
  oddRow: {
    backgroundColor: "#eeeeee",
    opacity: "100%",
  },
  evenRow: {
    backgroundColor: "#dddddd",
    opacity: "100%",
  },
}));

// Returns the customer's contacts, falling back to the legacy flat
// fields (contactfirstname/contactlastname/mobile/emailaddress) if
// the customer hasn't been re-saved under the new contacts table yet.
const getContactsList = (customer) => {
  if (Array.isArray(customer.contacts) && customer.contacts.length > 0) {
    return customer.contacts;
  }

  if (
    customer.contactfirstname ||
    customer.contactlastname ||
    customer.mobile ||
    customer.emailaddress
  ) {
    return [
      {
        firstname: customer.contactfirstname || "",
        lastname: customer.contactlastname || "",
        email: customer.emailaddress || "",
        phone: customer.mobile || "",
      },
    ];
  }

  return [];
};

const contactMatchesSearch = (contact, searchText) => {
  const fullName = `${contact.firstname || ""} ${contact.lastname || ""}`
    .trim()
    .toLowerCase();
  return (
    fullName.includes(searchText) ||
    (contact.email && contact.email.toLowerCase().includes(searchText)) ||
    (contact.phone && contact.phone.toLowerCase().includes(searchText))
  );
};

const CustomerList = ({
  customers,
  searchText,
  onCustomerSelect,
  deliveryMethods,
}) => {
  const loading = useSelector((state) => state.customers.loading);
  const error = useSelector((state) => state.customers.error);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [showMessage, setShowMessage] = useState();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [totalPages, setTotalPages] = useState();
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState();

  const classes = useStyles();

  const filterCustomers = (customerList) =>
    customerList
      .filter((p) =>
        selectedCustomerId.length > 0
          ? !selectedCustomerId.includes(p.id)
          : true
      )
      .filter((customer) => {
        if (customer.name && customer.name.toLowerCase().includes(searchText)) {
          return true;
        }
        return getContactsList(customer).some((contact) =>
          contactMatchesSearch(contact, searchText)
        );
      });

  useEffect(() => {
    const fcustomers = filterCustomers(customers.customers);
    let index =
      (page - 1) * itemsPerPage > fcustomers.length - 1
        ? 0
        : (page - 1) * itemsPerPage;
    setStartIndex(index);
    setEndIndex(index + itemsPerPage);

    setFilteredCustomers(fcustomers);
    setTotalPages(Math.ceil(fcustomers.length / itemsPerPage));
  }, [searchText, customers]);

  useEffect(() => {
    let index = (page - 1) * itemsPerPage;
    setStartIndex(index);
    setEndIndex(index + itemsPerPage);
  }, [page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlecloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    setSelectedCustomerId(selectedCustomerId.filter((i, index) => index !== 0));
  };

  const deleteCustomerById = (id) => {
    deleteCustomer(id)
      .then((res) => {
        setShowMessage(true);
        setMessage("Customer deleted successfully");

        const fcustomers = filterCustomers(customers.customers);
        setFilteredCustomers(fcustomers);
        setTotalPages(Math.ceil(fcustomers.length / itemsPerPage));

        let index =
          (page - 1) * itemsPerPage > fcustomers.length - 1
            ? 0
            : (page - 1) * itemsPerPage;
        setStartIndex(index);
        setEndIndex(index + itemsPerPage);
      })
      .catch((err) => {
        console.log("errrr", err);
        setMessage(err?.response?.data?.message);
        setShowMessage(true);
      });
  };

  if (!filteredCustomers || filteredCustomers.length === 0) {
    return (
      <div className="no-orders-found-text">
        <h3>No customer found</h3>
        {showMessage && (
          <div>
            <Snackbar
              open={showMessage}
              autoHideDuration={1200}
              onClose={() => {
                setShowMessage(false);
              }}
              message="Done"
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}>
              <Alert
                severity={`${
                  message.includes("successfully") ? "success" : "error"
                }`}
                variant="filled">
                {message}
              </Alert>
            </Snackbar>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <CircularProgress variant="determinate" value={50} valueBuffer={20} />
    );
  }

  if (error) {
    return <p>Error: {error.message}</p>;
  }

  return (
    <div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>Name</TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Mobile No</TableCell>
              <TableCell>Email Address</TableCell>
              <TableCell>Delivery Method</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers
              .slice(startIndex, endIndex)
              .map((customer, index) => {
                const contactsList = getContactsList(customer);
                const primaryContact = contactsList[0] || {};
                const extraContacts = contactsList.length - 1;
                const primaryName = `${primaryContact.firstname || ""} ${
                  primaryContact.lastname || ""
                }`.trim();

                return (
                  <TableRow
                    onClick={() => onCustomerSelect(customer)}
                    key={customer.id}
                    className={`${classes.tableRow} ${
                      customer.isSelected
                        ? "highlighted-row"
                        : index % 2 === 0
                        ? classes.evenRow
                        : classes.oddRow
                    }`}>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>
                      {primaryName}
                      {extraContacts > 0 ? ` (+${extraContacts} more)` : ""}
                    </TableCell>
                    <TableCell>{primaryContact.phone}</TableCell>
                    <TableCell>{primaryContact.email}</TableCell>
                    <TableCell>
                      {findMetaData(customer.deliverymethod, deliveryMethods)
                        ?.description || "-"}
                    </TableCell>
                    <TableCell>
                      <>
                        <IconButton
                          aria-label="delete"
                          style={{ color: "#990000" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCustomerId([
                              customer.id,
                              ...selectedCustomerId,
                            ]);
                            setOpenConfirmDelete(true);
                          }}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        renderItem={(item) => (
          <PaginationItem component="button" type="button" {...item} />
        )}
      />
      {showMessage && (
        <div>
          <Snackbar
            open={showMessage}
            autoHideDuration={1200}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
            onClose={() => {
              setShowMessage(false);
            }}
            message="Done">
            <Alert
              severity={`${
                message.includes("successfully") ? "success" : "error"
              }`}
              variant="filled">
              {message}
            </Alert>
          </Snackbar>
        </div>
      )}
      {openConfirmDelete && (
        <ConfirmBox
          confirmAction={() => {
            deleteCustomerById(selectedCustomerId[0]);
            setOpenConfirmDelete(false);
          }}
          cancelAction={handlecloseConfirmDelete}
        />
      )}
    </div>
  );
};

export default CustomerList;
